import BodyHeader from "../../components/customComp/BodyHeader";
import React, { useEffect, useState, useCallback } from "react";
import StatusOverview from "./dashboard/StatusOverview";
import CaseComparison from "./dashboard/CaseComparison";
import PieChartsRow from "./dashboard/PieChartsRow";
import CasePerformance from "./dashboard/CasePerformance";
import CustomDropdown from "../../components/input/CustomDropdown";
import SVGraphics from "../../assets/SVGraphics";
import { exportDashboardToPDF } from "../../utils/dashboardExportHelper";
import { authenticationService } from "../../services/AuthinticationService";

const DashboardComp = (props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState({
    statusOverview: { users: {}, cases: {} },
    casePerformance: [],
    caseComparison: [],
    findingsBreakdown: [],
  });

  const [siteId, setSiteId] = useState(null); // set to null to prevent fetching dashboard data for all sites on reload and initial load
  const [range, setRange] = useState(30);
  const [siteOptions, setSiteOptions] = useState([
    { value: "all", label: "All Sites" },
  ]);

  // Calculate the actual total from cases data for single vs multiple display
  const getTotalCasesForSite = (siteName) => {
    const siteData = dashboardData?.statusOverview?.cases?.perSite?.find(
      site => site.siteName === siteName
    );
    return siteData?.cases || 0;
  };

  const singleVsMulti = {
    Total: "Toatl cases",
    dot1: "Single", 
    dot2: "Multiple",
  };

  const casesWithFindings = {
    Total: "Toatl findings",
    dot1: "Algo findings",
    dot2: "Radiologis findings",
  };

  useEffect(() => {
    const fetchSites = async () => {

      // if the user is super admin, fetch all sites
      try {
        if(authenticationService.currentUserValue.profile.Role == "SuperAdmin"){
          const res = await props.restApi.getAllSites();
          const dynamicSites = res.data || [];
  
          const options = [
            { value: "all", label: "All Sites" },
            ...dynamicSites.map((site) => ({
              value: site.Site_name,
              label: site.Site_name,
            })),
          ];
        setSiteOptions(options);
        // if the user is admin, fetch the user's site (admin can only see his own site)
        } else if(authenticationService.currentUserValue.profile.Role == "Admin"){
          const options = [{ value: authenticationService.currentUserValue.profile.HCP, label: authenticationService.currentUserValue.profile.HCP }];
          setSiteOptions(options);  
        }

      } catch (err) {
        console.error("Failed to load sites:", err);
      }
    };

    fetchSites();
  }, []);

  const daysOptions = [
    { value: 30, label: "Last 30 Days" },
    { value: 60, label: "Last 60 Days" },
    { value: 90, label: "Last 90 Days" },
  ];

  // Fetch dashboard data based on filters
  const fetchData = useCallback(async () => {
    debugger
    // if user is not super admin, fetch dashboard data for the user's site
    if (authenticationService.currentUserValue.profile.Role != "SuperAdmin") {
      setSiteId(authenticationService.currentUserValue.profile.HCP);
    }
    else if (authenticationService.currentUserValue.profile.Role == "SuperAdmin") {
      setSiteId("all");
    }
    // if site is not null, fetch dashboard data for the site
    if (siteId !== null) {
      try {
        setIsLoading(true);
        const res = await props.restApi.getAllDashboardData(siteId, range);
        setDashboardData(res.data || {});
      } catch (err) {
        console.error("Failed to fetch dashboard data:", err);
        setDashboardData({
          statusOverview: { users: {}, cases: {} },
          casePerformance: [],
          caseComparison: [],
          findingsBreakdown: [],
        });
      } finally {
        setIsLoading(false);
      }
    }

  }, [siteId, range, props.restApi]);

  // Fetch data when component mounts or filters change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDropdownChange = (name, value) => {
    if (name === "site") setSiteId(value);
    if (name === "range") setRange(value);
  };

  const handleExportPDF = async () => {
    const siteLabel = siteOptions.find(opt => opt.value === siteId)?.label || "All Sites";
    const filename = `dashboard-report-${siteLabel.replace(/\s+/g, '-')}-${range}days-${new Date().toISOString().split('T')[0]}.pdf`;
    await exportDashboardToPDF(dashboardData, siteId, range, filename);
  };


  if (isLoading) return <div className="text-white">Loading dashboard...</div>;

  return (
    <div className="w-full px-12 pt-4 pb-16 h-full max-h-full overflow-y-auto box-border bg-[#161616]">
      <div className="flex justify-between w-100 items-center">
        <BodyHeader title={"Dashboard"} />
        <div className="flex gap-4 items-center">
          {/* Site Dropdown */}
          <CustomDropdown
            name="site"
            placeholder="Select Site"
            data={siteOptions}
            value={siteId}
            onChange={handleDropdownChange}
            variant="outlined"
            containerClassName="w-[117px] h-[33px] border-white text-[14px]"
          />

          <CustomDropdown
            name="range"
            placeholder="Select Range"
            data={daysOptions}
            value={range}
            onChange={handleDropdownChange}
            variant="outlined"
            icon={
              <SVGraphics svgname="calendar" className="w-4 h-4 mr-[5px]" />
            }
            containerClassName="w-[226px] h-[33px] border-white text-[14px]"
          />
        </div>
      </div>

      <div className="mt-4 py-6 flex-col gap-y-8">
        <div className="bg-[#111111] pt-7 pb-9 px-10">
          <StatusOverview
            users={dashboardData.statusOverview.users}
            cases={dashboardData.statusOverview.cases}
          />
        </div>

        <div className="bg-[#111111] pt-7 pb-9 px-10 mt-5">
          <CasePerformance casePerformance={dashboardData.casePerformance} />
        </div>

        <div className="bg-[#111111] pt-7 pb-9 px-10 mt-5">
          <CaseComparison caseComparison={dashboardData.caseComparison} />
        </div>

        <div className="bg-[#111111] pt-7 pb-9 px-10 mt-5">
          <PieChartsRow
            title={"Single Vs. Multiple Cases"}
            hasOptions={false}
            pieData={dashboardData.singleVsMultipleCases}
            loading={isLoading}
            text={singleVsMulti}
            getTotalForSite={getTotalCasesForSite}
          />
        </div>

        <div className="bg-[#111111] pt-7 pb-9 px-10 mt-5">
          <PieChartsRow
            title={"Cases with findings"}
            hasOptions={false}
            pieData={dashboardData.findingsBreakdown}
            loading={isLoading}
            text={casesWithFindings}
          />
        </div>
      </div>

      {/* Floating Export PDF Button */}
      <button
        onClick={handleExportPDF}
        disabled={isLoading}
        className={`fixed bottom-4 right-4 z-50 transition-opacity duration-150 ${
          isLoading ? "opacity-50 pointer-events-none" : "opacity-100"
        } flex items-center justify-center gap-2 w-[187px] h-[42px] bg-[#5A3EFF] text-white text-[14px] font-semibold rounded-[5px] hover:bg-[#4A2EEF] transition-colors disabled:cursor-not-allowed shadow-lg`}
      >
        {/* <SVGraphics svgname="download" className="w-4 h-4" /> */}
        Export PDF
      </button>
    </div>
  );
};

export default DashboardComp;
